import { forwardRef, useImperativeHandle, useRef ,useEffect ,useState} from "react";
import { ToggleView } from "../views/toggle-view";
import { InvalidInputMessage } from "./invalid-input";

export const InputNumber = forwardRef(({
  dataModel,
  name,
  label,
  hint,
  defaultValue,
  required,
  readOnly,
  disabled,
  autoComplete,
  onChange,
  errorsModel,
  showLabel = false,
  className = "",
  prefix = "",
  decimal = 2,
  allowNegative = false,
  placeholder,
  value: externalValue,  // Accept external value from parent
  onKeyDown,
  onInput,
}, ref) => {
  const hintId = `${name}Hint`;
  const model = dataModel || {};
  const [internalValue, setInternalValue] = useState(externalValue || (model[name] ?? defaultValue ?? "").toString());

  // Sync the internal value with the external value when it changes
  useEffect(() => {
    if (externalValue !== undefined) {
      setInternalValue(externalValue);
    }
  }, [externalValue]);

  const [onFocus, setOnFocus] = useState(false);
  const errorMessage = errorsModel && errorsModel[name];
  const ac = autoComplete ?? "on";

  const inputRef = useRef(null);
  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    select: () => inputRef.current?.select(),
    get input() { return inputRef.current; }
  }), []);

  const sanitize = (raw) => {
    if (raw == null) return "";
    let s = String(raw).replace(",", "."); 
    s = s.replace(/[^\d\.\-]/g, "");

    if (!allowNegative) s = s.replace(/\-/g, "");
    else {
      s = s.replace(/\-/g, "");
      if (String(raw).startsWith("-")) s = "-" + s;
    }

    const parts = s.split(".");
    if (parts.length > 2) s = parts.shift() + "." + parts.join("");

    if (decimal >= 0 && s.includes(".")) {
      const [i, d] = s.split(".");
      s = i + "." + d.slice(0, decimal);
    }

    if (s === "." || s === "-.") s = s.replace(".", "0.");
    if (s.startsWith(".")) s = "0" + s;
    if (s === "-") return allowNegative ? "-" : "";

    return s;
  };

  const handleChange = (e) => {
    const next = sanitize(e.target.value);
    setInternalValue(next);  // Update the internal value
    onChange?.({
      ...e,
      target: { ...e.target, name, value: next },
    });
  };

  const allowKey = (e) => {
    const k = e.key;
    const ctrlLike = e.ctrlKey || e.metaKey;
    const allowed =
      ["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"].includes(k) ||
      (ctrlLike && ["a","c","v","x","A","C","V","X"].includes(k)) ||
      (/^\d$/.test(k)) ||
      k === "." || k === "," ||
      (allowNegative && k === "-");

    if (!allowed) e.preventDefault();
  };

  return (
    <div className="mb-2">
      {showLabel && (
        <label htmlFor={name} className="block mb-1 text-white text-sm">
          {label || name}{required ? " *" : ""}
        </label>
      )}

      <div
        className={[
          "w-full h-[50px] flex items-center px-4",
          "rounded-[16px] bg-background-secondary",
          "ring-1 ring-[#34353F]",
          onFocus ? " outline-none ring-2 ring-white " : "  ",
          errorMessage ? "ring-chilli" : "",
          className,
        ].join(" ")}
      >
        {prefix ? (
          <span className="mr-2 text-white font-headings-primary text-[18px]">{prefix}</span>
        ) : null}

        <input
          ref={inputRef}
          required={required}
          readOnly={readOnly}
          disabled={disabled}
          autoComplete={ac}
          type="text"
          inputMode="decimal"
          pattern="[0-9]*"
          name={name}
          id={name}
          style={{ MozAppearance: "textfield" }}
          aria-describedby={hintId}
          value={internalValue}
          onClick={(e) => e.currentTarget.select()}
          onFocus={(e) => { e.currentTarget.select(); setOnFocus(true);}}
          onBlur={() => setOnFocus(false)}
          onWheel={(e) => e.currentTarget.blur()}
          onKeyDown={(e) => {
            allowKey(e);
            if (!e.defaultPrevented && typeof onKeyDown === 'function') onKeyDown(e);
          }}
          onInput={(e) => {
            if (typeof onInput === 'function') onInput(e);
          }}
          onChange={handleChange}
          placeholder={showLabel ? undefined : (placeholder || label)}
          className="w-full bg-transparent outline-none text-white/90 placeholder:text-white/50  text-base font-headings-primary "
        />
      </div>

      <InvalidInputMessage errorMessage={errorMessage} />
      <ToggleView show={!!hint}>
        <div id={hintId} className="mt-1 text-xs text-white/60">
          {hint}
        </div>
      </ToggleView>
    </div>
  );
});

